// src/pages/AlarmDetails/index.jsx
// Alarm Details Page with tabbed interface for comprehensive alarm information
// Supports both authenticated access (by ID) and public access (by UUID)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Divider,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  LocalHospital as EmergencyIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  Code as DeveloperIcon,
  Timer as TimerIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { alarmsApi } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useMonitoringWebSocket } from '../../hooks/useMonitoringWebSocket';
import { useIsMobile } from '../../hooks/useResponsive';

// Tab Components
import OverviewTab from './tabs/OverviewTab';
import LocationTab from './tabs/LocationTab';
import HistoryTab from './tabs/HistoryTab';
import DeveloperTab from './tabs/DeveloperTab';

// Status color mapping
const getStatusColor = (stateCode) => {
  switch (stateCode) {
    case 'countdown':
      return 'warning';
    case 'sent_to_emergency':
      return 'error';
    case 'acknowledged':
      return 'info';
    case 'dispatched':
      return 'primary';
    case 'finished':
      return 'success';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
};

// Status icon mapping
const getStatusIcon = (stateCode) => {
  switch (stateCode) {
    case 'countdown':
      return <TimerIcon />;
    case 'sent_to_emergency':
      return <ErrorIcon />;
    case 'acknowledged':
      return <InfoIcon />;
    case 'dispatched':
      return <EmergencyIcon />;
    case 'finished':
      return <CheckCircleIcon />;
    case 'cancelled':
      return <WarningIcon />;
    default:
      return <InfoIcon />;
  }
};

// Helper to detect if a string is a UUID format
const isUUID = (str) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const AlarmDetails = () => {
  // Support both /alarms/:id (authenticated) and /alarm/:uuid (public)
  // Also detect if :id param is actually a UUID and treat it as public access
  const { id, uuid } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const isMobile = useIsMobile();

  // Determine if this is public (UUID) or authenticated (ID) access
  // If the id parameter looks like a UUID, treat it as public UUID access
  const detectedUUID = uuid || (id && isUUID(id) ? id : null);
  const isPublicAccess = !!detectedUUID;
  const alarmIdentifier = detectedUUID || id;

  const [alarm, setAlarm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [statusNotification, setStatusNotification] = useState(null); // For showing status change notifications

  // Handle WebSocket messages for real-time updates
  const handleWebSocketMessage = useCallback((message) => {
    // Only process if this message relates to our alarm
    if (message.type === 'alarm_update' || message.type === 'alarm_status_change' || message.type === 'alarm_closed') {
      const messageAlarmId = message.alarmId || message.alarm_id || message.data?.id;
      const messageAlarmUUID = message.alarmUUID || message.alarm_UUID || message.data?.alarm_UUID;

      if (
        (messageAlarmId && String(messageAlarmId) === String(alarmIdentifier)) ||
        (messageAlarmUUID && messageAlarmUUID === alarmIdentifier)
      ) {
        console.log('[AlarmDetails] Received update for this alarm:', message);

        // Handle closed alarm event
        if (message.type === 'alarm_closed') {
          setStatusNotification({
            severity: 'info',
            message: 'This alarm has been resolved.'
          });
          // For public access, show access denied after a delay
          if (isPublicAccess) {
            setTimeout(() => {
              setAccessDenied(true);
              setError('This alarm is no longer accessible.');
            }, 2000);
          }
          return;
        }

        // If alarm became terminal (closed), show notification
        if (message.data?.alarmStateModel?.isTerminal || message.status === 'finished' || message.status === 'cancelled') {
          const newStatus = message.data?.alarmStateModel?.friendlyName || message.status;
          setStatusNotification({
            severity: 'success',
            message: `Alarm status changed to: ${newStatus}`
          });
          setAlarm(prev => prev ? { ...prev, ...message.data, alarmStateModel: message.data?.alarmStateModel || prev?.alarmStateModel } : prev);
        } else {
          // Show notification for non-terminal status changes
          if (message.status || message.data?.alarmStateModel?.code) {
            const newStatus = message.data?.alarmStateModel?.friendlyName || message.status;
            setStatusNotification({
              severity: 'info',
              message: `Alarm status updated: ${newStatus}`
            });
          }
          // Reload the alarm data for fresh state
          loadAlarm();
        }
      }
    }
  }, [alarmIdentifier, isPublicAccess]);

  // WebSocket for real-time updates (only for authenticated users, not public access)
  const { isConnected } = useMonitoringWebSocket({
    autoConnect: !isPublicAccess,
    onMessage: handleWebSocketMessage
  });

  // Load alarm data
  const loadAlarm = useCallback(async () => {
    try {
      setError(null);
      setAccessDenied(false);

      let response;
      if (isPublicAccess) {
        // Public access via UUID - no auth required
        response = await alarmsApi.getAlarmByUUID(alarmIdentifier);
      } else {
        // Authenticated access via ID
        response = await alarmsApi.getAlarmById(alarmIdentifier);
      }

      setAlarm(response.data?.data || null);
    } catch (err) {
      console.error('Failed to load alarm:', err);

      // Check if access was denied (terminal state for public access)
      if (err.response?.status === 403) {
        setAccessDenied(true);
        setError(err.response?.data?.error || 'This alarm is no longer accessible.');
      } else if (err.response?.status === 404) {
        setError('Alarm not found.');
      } else {
        setError('Failed to load alarm details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [alarmIdentifier, isPublicAccess]);

  // Initial load
  useEffect(() => {
    loadAlarm();
  }, [loadAlarm]);

  // Auto-refresh for active alarms (fallback when WebSocket not connected)
  useEffect(() => {
    // Skip polling if WebSocket is connected (we get real-time updates)
    if (isConnected) {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
      return;
    }

    if (alarm && !alarm.alarmStateModel?.isTerminal) {
      // Refresh every 5 seconds for countdown state, 30 seconds otherwise
      const interval = alarm.alarmStateModel?.code === 'countdown' ? 5000 : 30000;
      const timer = setInterval(loadAlarm, interval);
      setRefreshInterval(timer);
      return () => clearInterval(timer);
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [alarm?.alarmStateModel?.isTerminal, alarm?.alarmStateModel?.code, loadAlarm, isConnected]);

  // Handle status update
  // comment is required for terminal states (finished, cancelled)
  const handleUpdateStatus = async (newStatus, comment = null) => {
    try {
      if (isPublicAccess) {
        // Public access - use UUID endpoint
        await alarmsApi.updateAlarmStatusByUUID(alarm.alarm_UUID, newStatus, comment);
      } else {
        // Authenticated access - use ID endpoint
        await alarmsApi.updateAlarmStatus(alarm.id, newStatus, comment);
      }
      loadAlarm();
    } catch (err) {
      console.error('Failed to update alarm status:', err);
      if (err.response?.status === 403) {
        setError('This alarm can no longer be updated.');
      } else if (err.response?.status === 400 && err.response?.data?.error?.includes('comment')) {
        setError('A comment is required to finish or cancel an alarm.');
      } else {
        setError(err.response?.data?.error || 'Failed to update alarm status. Please try again.');
      }
    }
  };

  // Show loading state - but for public access, don't wait for auth to finish
  if (loading || (!isPublicAccess && authLoading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !alarm) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          {accessDenied ? (
            <>
              <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Alarm No Longer Accessible
              </Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This alarm has been resolved and is no longer available for public access.
                If you need to view historical alarm data, please log in to the portal.
              </Typography>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Unable to Load Alarm
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
              </Alert>
            </>
          )}
          {isAuthenticated ? (
            <Button variant="outlined" onClick={() => navigate('/alarms')}>
              Back to Alarms
            </Button>
          ) : (
            <Button variant="outlined" onClick={() => navigate('/login')}>
              Log In
            </Button>
          )}
        </Paper>
      </Box>
    );
  }

  if (!alarm) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Alarm not found
        </Alert>
        {isAuthenticated && (
          <Button variant="outlined" onClick={() => navigate('/alarms')} sx={{ mt: 2 }}>
            Back to Alarms
          </Button>
        )}
      </Box>
    );
  }

  const isActive = !['finished', 'cancelled'].includes(alarm.alarmStateModel?.code);
  const isCountdown = alarm.alarmStateModel?.code === 'countdown';

  // Handle back navigation based on access type
  const handleBack = () => {
    if (isAuthenticated) {
      navigate('/alarms');
    } else {
      // For public access, just go back in history or home
      window.history.length > 1 ? navigate(-1) : navigate('/');
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Breadcrumbs - only show for authenticated users on desktop */}
      {isAuthenticated && !isMobile && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/alarms" color="inherit" underline="hover">
            Alarms
          </Link>
          <Typography color="text.primary">
            {alarm.friendlyName || `Alarm #${alarm.id}`}
          </Typography>
        </Breadcrumbs>
      )}

      {/* Public access banner */}
      {isPublicAccess && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isMobile ? 'Public access - limited actions' : 'You are viewing this alarm via public access link. Actions are limited to status updates only.'}
        </Alert>
      )}

      {/* Header */}
      <Paper
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 2, md: 3 },
          backgroundColor: isCountdown ? 'warning.light' :
                          alarm.alarmStateModel?.code === 'sent_to_emergency' ? 'error.light' :
                          'background.paper',
          borderLeft: { xs: 4, md: 6 },
          borderColor: isCountdown ? 'warning.main' :
                       alarm.alarmStateModel?.code === 'sent_to_emergency' ? 'error.main' :
                       'primary.main'
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          gap: { xs: 1.5, sm: 2 }
        }}>
          <Box sx={{ width: '100%' }}>
            {/* Mobile: Back button on its own row */}
            {isMobile && isAuthenticated && (
              <IconButton onClick={handleBack} size="small" sx={{ mb: 1, ml: -1 }}>
                <BackIcon />
              </IconButton>
            )}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              mb: 1,
              flexWrap: 'wrap'
            }}>
              {/* Desktop: Back button inline */}
              {!isMobile && isAuthenticated && (
                <IconButton onClick={handleBack} size="small">
                  <BackIcon />
                </IconButton>
              )}
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <EmergencyIcon color="error" fontSize={isMobile ? 'medium' : 'large'} />
                {alarm.friendlyName || `Alarm #${alarm.id}`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={getStatusIcon(alarm.alarmStateModel?.code)}
                  label={alarm.alarmStateModel?.friendlyName || 'Unknown'}
                  color={getStatusColor(alarm.alarmStateModel?.code)}
                  size={isMobile ? 'small' : 'medium'}
                />
                {isActive && (
                  <Chip
                    label="ACTIVE"
                    color="error"
                    variant="outlined"
                    size="small"
                    sx={{ animation: 'pulse 1.5s infinite' }}
                  />
                )}
              </Box>
            </Box>
            <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
              {alarm.alarmTypeModel?.friendlyName || 'Emergency Alarm'}
              {!isMobile && ` | UUID: ${alarm.alarm_UUID}`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadAlarm} size={isMobile ? 'small' : 'medium'}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Tabs - Developer tab hidden for public access */}
      <Paper sx={{ mb: { xs: 1.5, md: 2 } }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: { xs: 40, sm: 48 },
            '& .MuiTab-root': {
              minHeight: { xs: 40, sm: 48 },
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}
        >
          <Tab
            icon={<ScheduleIcon fontSize={isMobile ? 'small' : 'medium'} />}
            iconPosition="start"
            label={isMobile ? 'Overview' : 'Overview'}
          />
          <Tab
            icon={<LocationIcon fontSize={isMobile ? 'small' : 'medium'} />}
            iconPosition="start"
            label="Location"
          />
          <Tab
            icon={<HistoryIcon fontSize={isMobile ? 'small' : 'medium'} />}
            iconPosition="start"
            label="History"
          />
          {/* Developer tab only shown for authenticated users */}
          {!isPublicAccess && (
            <Tab
              icon={<DeveloperIcon fontSize={isMobile ? 'small' : 'medium'} />}
              iconPosition="start"
              label={isMobile ? 'Dev' : 'Developer'}
            />
          )}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {tabValue === 0 && (
          <OverviewTab
            alarm={alarm}
            onUpdateStatus={handleUpdateStatus}
            onRefresh={loadAlarm}
          />
        )}
        {tabValue === 1 && (
          <LocationTab alarm={alarm} isPublicAccess={isPublicAccess} />
        )}
        {tabValue === 2 && (
          <HistoryTab alarm={alarm} />
        )}
        {/* Developer tab only for authenticated users */}
        {tabValue === 3 && !isPublicAccess && (
          <DeveloperTab alarm={alarm} />
        )}
      </Box>

      {/* Add CSS for pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>

      {/* Status change notification */}
      <Snackbar
        open={!!statusNotification}
        autoHideDuration={4000}
        onClose={() => setStatusNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {statusNotification && (
          <Alert
            severity={statusNotification.severity}
            onClose={() => setStatusNotification(null)}
            sx={{ width: '100%' }}
          >
            {statusNotification.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default AlarmDetails;
